export const adminCrudConfig = {
  categories: {
    singular: 'Category',
    description:
      'Manage category names, images, sort order, and active status based on the `categories` table.',
    note:
      'This maps to the category records in your schema and is ready for add, edit, update, and delete flows.',
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'icon', label: 'Icon', type: 'text' },
      { name: 'image', label: 'Image URL', type: 'text' },
      { name: 'sort_order', label: 'Sort Order', type: 'number' },
      {
        name: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { label: 'Active', value: '1' },
          { label: 'Inactive', value: '0' },
        ],
      },
    ],
    initialItems: [
      { id: 1, name: 'Classic Papad', icon: 'local_dining', image: '/images/classic.png', sort_order: 1, status: '1' },
      { id: 2, name: 'Masala Papad', icon: 'restaurant_menu', image: '/images/masala.png', sort_order: 2, status: '1' },
    ],
  },
  products: {
    singular: 'Product',
    description:
      'Manage product catalog records from the `products` table including price, MRP, weight, stock, and bestseller state.',
    note:
      'This page follows the product schema shown in the DB design and can be wired to backend APIs next.',
    fields: [
      { name: 'category_id', label: 'Category ID', type: 'number', required: true },
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'image', label: 'Image URL', type: 'text' },
      { name: 'price', label: 'Price', type: 'number', required: true },
      { name: 'mrp', label: 'MRP', type: 'number' },
      { name: 'weight', label: 'Weight', type: 'text' },
      { name: 'stock', label: 'Stock', type: 'number' },
      {
        name: 'is_best_selling',
        label: 'Best Seller',
        type: 'select',
        options: [
          { label: 'Yes', value: '1' },
          { label: 'No', value: '0' },
        ],
      },
      {
        name: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { label: 'Active', value: '1' },
          { label: 'Inactive', value: '0' },
        ],
      },
    ],
    initialItems: [
      { id: 101, category_id: 1, name: 'Punjabi Papad', description: 'Spiced lentil papad', image: '/images/punjabi.png', price: '120', mrp: '140', weight: '200g', stock: '45', is_best_selling: '1', status: '1' },
      { id: 102, category_id: 2, name: 'Jeera Papad', description: 'Cumin flavored papad', image: '/images/jeera.png', price: '95', mrp: '110', weight: '180g', stock: '32', is_best_selling: '0', status: '1' },
    ],
  },
  variants: {
    singular: 'Variant',
    description:
      'Manage `product_variants` records such as SKU, pricing, weight, image, and availability for each product.',
    note:
      'Variant-level controls help you manage pack sizes and different sellable combinations without changing the product master.',
    fields: [
      { name: 'product_id', label: 'Product ID', type: 'number', required: true },
      { name: 'variant_name', label: 'Variant Name', type: 'text', required: true },
      { name: 'sku', label: 'SKU', type: 'text', required: true },
      { name: 'price', label: 'Price', type: 'number', required: true },
      { name: 'mrp', label: 'MRP', type: 'number' },
      { name: 'weight', label: 'Weight', type: 'text' },
      { name: 'image', label: 'Image URL', type: 'text' },
      {
        name: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { label: 'Active', value: '1' },
          { label: 'Inactive', value: '0' },
        ],
      },
    ],
    initialItems: [
      { id: 201, product_id: 101, variant_name: '200g Pack', sku: 'PUN-200', price: '120', mrp: '140', weight: '200g', image: '/images/punjabi-200.png', status: '1' },
      { id: 202, product_id: 101, variant_name: '500g Family Pack', sku: 'PUN-500', price: '280', mrp: '320', weight: '500g', image: '/images/punjabi-500.png', status: '1' },
    ],
  },
  inventory: {
    singular: 'Inventory Item',
    description:
      'Manage stock and reserved stock from the `inventory` table for each variant in the catalog.',
    note:
      'This keeps inventory tied to variants so admins can track available and reserved quantities cleanly.',
    fields: [
      { name: 'variant_id', label: 'Variant ID', type: 'number', required: true },
      { name: 'stock', label: 'Stock', type: 'number', required: true },
      { name: 'reserved_stock', label: 'Reserved Stock', type: 'number' },
      { name: 'updated_at', label: 'Updated At', type: 'datetime-local' },
    ],
    initialItems: [
      { id: 301, variant_id: 201, stock: '120', reserved_stock: '8', updated_at: '2026-03-18T10:30' },
      { id: 302, variant_id: 202, stock: '64', reserved_stock: '5', updated_at: '2026-03-18T11:15' },
    ],
  },
  orders: {
    singular: 'Order',
    description:
      'Manage order records from the `orders` table including customer references, totals, payment method, and delivery status.',
    note:
      'Orders can later be expanded with nested order items, addresses, and payment records from the related tables in your schema.',
    fields: [
      { name: 'user_id', label: 'User ID', type: 'number' },
      { name: 'guest_id', label: 'Guest ID', type: 'number' },
      { name: 'guest_name', label: 'Guest Name', type: 'text' },
      { name: 'guest_mobile', label: 'Guest Mobile', type: 'text' },
      { name: 'total_amount', label: 'Total Amount', type: 'number', required: true },
      { name: 'delivery_charge', label: 'Delivery Charge', type: 'number' },
      { name: 'discount', label: 'Discount', type: 'number' },
      { name: 'final_amount', label: 'Final Amount', type: 'number', required: true },
      { name: 'payment_method', label: 'Payment Method', type: 'text' },
      { name: 'payment_status', label: 'Payment Status', type: 'text' },
      { name: 'order_status', label: 'Order Status', type: 'text' },
      { name: 'delivery_date', label: 'Delivery Date', type: 'date' },
      { name: 'delivery_slot_id', label: 'Delivery Slot ID', type: 'number' },
    ],
    initialItems: [
      { id: 401, user_id: '1', guest_id: '', guest_name: '', guest_mobile: '', total_amount: '540', delivery_charge: '40', discount: '30', final_amount: '550', payment_method: 'UPI', payment_status: 'Paid', order_status: 'Packed', delivery_date: '2026-03-19', delivery_slot_id: '1' },
      { id: 402, user_id: '', guest_id: '11', guest_name: 'Anu', guest_mobile: '9876543210', total_amount: '230', delivery_charge: '20', discount: '0', final_amount: '250', payment_method: 'COD', payment_status: 'Pending', order_status: 'Placed', delivery_date: '2026-03-20', delivery_slot_id: '2' },
    ],
  },
  users: {
    singular: 'User',
    description:
      'Manage user records from the `users` table including contact details, avatar, and active status.',
    note:
      'This matches the users table in your schema and is a good base for customer or admin account management.',
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'mobile', label: 'Mobile', type: 'text', required: true },
      { name: 'email', label: 'Email', type: 'email', required: true },
      { name: 'password', label: 'Password', type: 'text' },
      { name: 'avatar', label: 'Avatar URL', type: 'text' },
      {
        name: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { label: 'Active', value: '1' },
          { label: 'Inactive', value: '0' },
        ],
      },
    ],
    initialItems: [
      { id: 1, name: 'Vijay', mobile: '9876501234', email: 'vijay@pappad.com', password: '******', avatar: '/avatars/vijay.png', status: '1' },
      { id: 2, name: 'Meena', mobile: '9123456780', email: 'meena@pappad.com', password: '******', avatar: '/avatars/meena.png', status: '1' },
    ],
  },
  coupons: {
    singular: 'Coupon',
    description:
      'Manage coupon codes from the `coupons` table including discount type, value, minimum order, expiry, and status.',
    note:
      'Coupon rules from your DB schema are represented here with editable form inputs and a list view.',
    fields: [
      { name: 'code', label: 'Code', type: 'text', required: true },
      {
        name: 'discount_type',
        label: 'Discount Type',
        type: 'select',
        options: [
          { label: 'Percent', value: 'percent' },
          { label: 'Amount', value: 'amount' },
        ],
      },
      { name: 'discount_value', label: 'Discount Value', type: 'number', required: true },
      { name: 'min_order', label: 'Min Order', type: 'number' },
      { name: 'expiry_date', label: 'Expiry Date', type: 'date' },
      {
        name: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { label: 'Active', value: '1' },
          { label: 'Inactive', value: '0' },
        ],
      },
    ],
    initialItems: [
      { id: 501, code: 'WELCOME10', discount_type: 'percent', discount_value: '10', min_order: '199', expiry_date: '2026-04-15', status: '1' },
      { id: 502, code: 'FLAT50', discount_type: 'amount', discount_value: '50', min_order: '399', expiry_date: '2026-04-30', status: '1' },
    ],
  },
  banners: {
    singular: 'Banner',
    description:
      'Manage promotional banners from the `banners` table with title, image, link type, target reference, and status.',
    note:
      'This page is useful for homepage promotions and seasonal campaign publishing.',
    fields: [
      { name: 'title', label: 'Title', type: 'text', required: true },
      { name: 'image', label: 'Image URL', type: 'text', required: true },
      { name: 'link_type', label: 'Link Type', type: 'text' },
      { name: 'link_id', label: 'Link ID', type: 'number' },
      {
        name: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { label: 'Active', value: '1' },
          { label: 'Inactive', value: '0' },
        ],
      },
    ],
    initialItems: [
      { id: 601, title: 'Summer Offer', image: '/banners/summer.jpg', link_type: 'category', link_id: '1', status: '1' },
      { id: 602, title: 'New Arrivals', image: '/banners/new.jpg', link_type: 'product', link_id: '101', status: '1' },
    ],
  },
  'delivery-slots': {
    singular: 'Delivery Slot',
    description:
      'Manage `delivery_slots` records with slot name, timing, order capacity, and active status.',
    note:
      'This mirrors the delivery slot structure visible in the database design and is ready for schedule management.',
    fields: [
      { name: 'slot_name', label: 'Slot Name', type: 'text', required: true },
      { name: 'start_time', label: 'Start Time', type: 'time', required: true },
      { name: 'end_time', label: 'End Time', type: 'time', required: true },
      { name: 'max_orders', label: 'Max Orders', type: 'number' },
      {
        name: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { label: 'Active', value: '1' },
          { label: 'Inactive', value: '0' },
        ],
      },
    ],
    initialItems: [
      { id: 1, slot_name: 'Morning Slot', start_time: '09:00', end_time: '12:00', max_orders: '40', status: '1' },
      { id: 2, slot_name: 'Evening Slot', start_time: '16:00', end_time: '20:00', max_orders: '55', status: '1' },
    ],
  },
  notifications: {
    singular: 'Notification',
    description:
      'Manage notification records from the `notifications` table including target user, title, message, type, and read state.',
    note:
      'This can later be extended into push, SMS, or email integrations while staying aligned with your current schema.',
    fields: [
      { name: 'user_id', label: 'User ID', type: 'number' },
      { name: 'guest_id', label: 'Guest ID', type: 'number' },
      { name: 'title', label: 'Title', type: 'text', required: true },
      { name: 'message', label: 'Message', type: 'textarea', required: true },
      { name: 'type', label: 'Type', type: 'text' },
      { name: 'reference_id', label: 'Reference ID', type: 'number' },
      {
        name: 'is_read',
        label: 'Read Status',
        type: 'select',
        options: [
          { label: 'Read', value: '1' },
          { label: 'Unread', value: '0' },
        ],
      },
    ],
    initialItems: [
      { id: 701, user_id: '1', guest_id: '', title: 'Order Dispatched', message: 'Your order is on the way.', type: 'order', reference_id: '401', is_read: '0' },
      { id: 702, user_id: '2', guest_id: '', title: 'Coupon Unlocked', message: 'A new discount coupon is available.', type: 'promotion', reference_id: '501', is_read: '1' },
    ],
  },
  reports: {
    singular: 'Report',
    description:
      'The DB image does not show a dedicated reports table, so this page uses report presets and exports as an inferred admin management screen.',
    note:
      'I inferred this page because reports were requested in the sidebar but no explicit reports table is visible in the schema screenshot.',
    fields: [
      { name: 'report_name', label: 'Report Name', type: 'text', required: true },
      {
        name: 'report_type',
        label: 'Report Type',
        type: 'select',
        options: [
          { label: 'Sales', value: 'sales' },
          { label: 'Orders', value: 'orders' },
          { label: 'Inventory', value: 'inventory' },
          { label: 'Users', value: 'users' },
        ],
      },
      { name: 'date_from', label: 'Date From', type: 'date' },
      { name: 'date_to', label: 'Date To', type: 'date' },
      {
        name: 'export_format',
        label: 'Export Format',
        type: 'select',
        options: [
          { label: 'PDF', value: 'pdf' },
          { label: 'Excel', value: 'xlsx' },
          { label: 'CSV', value: 'csv' },
        ],
      },
      {
        name: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { label: 'Ready', value: 'ready' },
          { label: 'Draft', value: 'draft' },
        ],
      },
    ],
    initialItems: [
      { id: 801, report_name: 'Weekly Sales Summary', report_type: 'sales', date_from: '2026-03-10', date_to: '2026-03-17', export_format: 'pdf', status: 'ready' },
      { id: 802, report_name: 'Low Stock Review', report_type: 'inventory', date_from: '2026-03-01', date_to: '2026-03-18', export_format: 'xlsx', status: 'draft' },
    ],
  },
  settings: {
    singular: 'Setting',
    description:
      'Manage application settings from the `settings` table as name-value pairs for global store behavior.',
    note:
      'This is shaped directly from the settings table in your DB design and works well for admin-level configuration management.',
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'value', label: 'Value', type: 'textarea', required: true },
    ],
    initialItems: [
      { id: 901, name: 'store_name', value: 'Pappad Store' },
      { id: 902, name: 'support_mobile', value: '9876501234' },
    ],
  },
};

export function getAdminCrudConfig(sectionId) {
  return adminCrudConfig[sectionId];
}
